import { Component, inject, Injectable, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
    Page,
    PageResult,
    QuerySortDirection,
    ReactiveSearchHomeComponent,
    SearchService,
    ServiceHandler,
} from '@nucleus/ui-base';
import { UserDataTypeService } from '../../services/userdata-type/userdata-type.service';
import { UserDataTypeSummary } from '../../services/userdata-type/userdata-type.models';

// TODO: page-help-context entry for /administration/userdata-types

/**
 * Filter envelope for the UserData types list. Just the omnibar
 * free-text — narrows the list to types whose name contains the
 * search (case-insensitive).
 */
export class UserDataTypeFilter {
    constructor(public value: string = '') {}
}

/**
 * Client-side adapter that lets {@link UserDataTypesComponent} extend
 * {@link ReactiveSearchHomeComponent}. The backend exposes only
 * {@code GET /api/userdata/types/observed} (aggregation endpoint, no
 * server-side filter) so the adapter wraps {@code UserDataTypeService
 * .listObserved} and applies filter + sort + pagination locally. The
 * dataset is the distinct-type taxonomy — small by design.
 */
@Injectable({ providedIn: 'root' })
export class UserDataTypeSearchAdapter implements SearchService<UserDataTypeSummary, UserDataTypeFilter> {

    private readonly api: UserDataTypeService = inject(UserDataTypeService);

    search(handler: ServiceHandler<UserDataTypeSummary[]>): void {
        this.api.listObserved(handler);
    }

    filter(handler: ServiceHandler<PageResult<UserDataTypeSummary>>, filter: UserDataTypeFilter, page?: Page): void {
        handler.onStart?.();
        this.api.listObserved({
            onStart:    () => {},
            onSuccess:  (rows) => {
                const sortField = page?.sortField ?? 'type';
                const sortAsc   = (page?.sortDirection ?? QuerySortDirection.ASC) === QuerySortDirection.ASC;
                const matched   = (rows ?? [])
                    .filter(t => this.matches(t, filter))
                    .sort((a, b) => this.compare(a, b, sortField, sortAsc));
                const total = matched.length;
                const size  = page?.pageSize ?? (total || 1);
                const start = (page?.pageNumber ?? 0) * size;
                const slice = page ? matched.slice(start, start + size) : matched;
                handler.onSuccess(new PageResult<UserDataTypeSummary>(slice, total, page?.pageNumber ?? 0));
            },
            onError:    (err) => handler.onError?.(err),
            onComplete: ()    => handler.onComplete?.(),
        });
    }

    private matches(t: UserDataTypeSummary, f: UserDataTypeFilter): boolean {
        const search = (f.value ?? '').trim().toLowerCase();
        if (!search) return true;
        return (t.type ?? '').toLowerCase().includes(search);
    }

    private compare(a: UserDataTypeSummary, b: UserDataTypeSummary, field: string, asc: boolean): number {
        const av = (a as any)[field] ?? '';
        const bv = (b as any)[field] ?? '';
        // Numeric fields compare numerically; string fields use locale-aware compare.
        const cmp = (typeof av === 'number' && typeof bv === 'number')
            ? av - bv
            : String(av).localeCompare(String(bv));
        return asc ? cmp : -cmp;
    }
}

/**
 * Admin grid over the distinct UserData types observed in the deployment.
 *
 * Read-mostly: types appear here automatically as ingested rows surface a
 * new value in user_data.type. The row count, attribute count, and template
 * counts come straight from the backend aggregation endpoint
 * ({@code GET /api/userdata/types/observed}) — there's no per-row work the
 * UI does client-side.
 *
 * <p>Migrated to {@code <lib-list-page>} + {@link ReactiveSearchHomeComponent}
 * for chrome consistency. The only filter is the omnibar free-text (matches
 * type name), so no {@code filters} slot — lib-list-page suppresses the
 * drawer when {@code [filters]} is empty.
 */
@Component({
    selector: 'app-userdata-types',
    templateUrl: './userdata-types.component.html',
    styleUrl: './userdata-types.component.css',
    standalone: false,
})
export class UserDataTypesComponent
    extends ReactiveSearchHomeComponent<UserDataTypeSummary, UserDataTypeFilter>
    implements OnInit {

    protected override router: Router = inject(Router);

    displayedColumns = ['type', 'rowCount', 'attributeCount', 'templateInCount',
                        'templateOutCount', 'lastActivity'];

    constructor() {
        super(inject(UserDataTypeSearchAdapter), 'type', QuerySortDirection.ASC);
    }

    // ── ReactiveSearchHomeComponent overrides ───────────────────────────────

    override createFilter(): UserDataTypeFilter {
        return new UserDataTypeFilter();
    }

    override onResetFilter(): void {
        this.filter.update(f => Object.assign(f, new UserDataTypeFilter()));
    }

    override isReadyForSearch(): boolean {
        // Reuse the existing user-data management gates — the same authority
        // that lets you see UserData rows lets you see the type taxonomy
        // they're classified under.
        return this.permissionChecker.hasAccessToUserDataTypeManagement();
    }

    ngOnInit(): void {
        if (!this.permissionChecker.hasAccessToUserDataTypeManagement()) {
            this.showAccessDeny();
        }
    }

    // ── row navigation ───────────────────────────────────────────────────────

    openType(t: UserDataTypeSummary): void {
        if (!t?.type) return;
        // Use Router directly rather than BaseComponent.navigateTo() so we
        // can pass the type as a path segment, matching the route
        // /administration/userdata-types/:type.
        this.router.navigate(['/administration/userdata-types', t.type]);
    }
}
